// client/src/components/ProtectedRoute.jsx
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function ProtectedRoute({
  children,
  requireAdmin = false,
  requireActiveMembership = false,
  requireTier, // string or array of strings: ["Business Membership", "Professional"]
}) {
  const {
    user,
    profile,
    membership,
    loading,
    ready,
    csrfToken,
    fetchCsrfToken,
    csrfLoaded,
  } = useAuth();

  const isAdmin = Boolean(profile?.is_admin);
  const hasActiveMembership = Boolean(membership?.has_active_membership);

  const navigate = useNavigate();
  const location = useLocation();
  const csrfFetchingRef = useRef(false);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Non-blocking CSRF fallback (never blocks rendering)
  useEffect(() => {
    if (loading || !user || csrfFetchingRef.current) return;
    if (!csrfLoaded && !csrfToken) {
      console.warn(`${new Date().toISOString()} - CSRF missing → fetching fallback`);
      csrfFetchingRef.current = true;
      fetchCsrfToken().finally(() => {
        csrfFetchingRef.current = false;
      });
    }
  }, [user, loading, csrfToken, csrfLoaded, fetchCsrfToken]);

  // ──────────────────────────────────────────────────────────────
  // ONE SOURCE OF TRUTH: All auth checks + redirects happen here
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    // Still loading core auth state → do nothing yet
    if (loading || !ready) return;

    // Not logged in
    if (!user) {
      navigate('/signin', { replace: true });
      return;
    }

    // Admin-only route
    if (requireAdmin && !isAdmin) {
      navigate('/estate-sales-pipeline', { replace: true });
      return;
    }

    // Non-admin users → enforce membership / tier rules
    if (!isAdmin) {
      if (requireActiveMembership && !hasActiveMembership) {
        navigate('/estate-sales-pipeline/member-maint2', { replace: true });
        return;
      }

      if (requireTier) {
        const allowedTiers = Array.isArray(requireTier) ? requireTier : [requireTier];
        const userTier = membership?.tier;

        if (!hasActiveMembership || !allowedTiers.includes(userTier)) {
          navigate('/estate-sales-pipeline/member-maint2', { replace: true });
          return;
        }
      }
    }
  }, [
    loading,
    ready,
    user,
    isAdmin,
    hasActiveMembership,
    membership?.tier,
    requireAdmin,
    requireActiveMembership,
    requireTier,
    navigate,
    isAdmin,
  ]);

  // ──────────────────────────────────────────────────────────────
  // Render logic — super simple now
  // ──────────────────────────────────────────────────────────────
  if (loading || !ready) {
    return null; // or <Spinner /> if you want
  }

  if (!user) {
    return null;
  }

  // At this point the useEffect above has already redirected if needed
  // So if we’re still here → user is fully authorized
  return <>{children}</>;
}

export default ProtectedRoute;